/**
 * cascader.jsx
 *
 * @Author: zhangce
 * @Date: 17/8/29 下午2:28
 */

import React, { cloneElement } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Input, Menu, MenuItem, ParallelMenu, DropdownMenu } from '../index';
import ExpandMore from '../icons/internals/ExpandMore';
import { arrayTreeFilter, treeToPath } from '../util/arrayTreeFilter';
import { isEqualArrays } from '../util/isEqual';

/**
 * Cascader
 * */
class Cascader extends React.PureComponent {
  static propTypes = {
    /**
     * 类前缀
     * */
    prefixCls: PropTypes.string,
    /**
     * 类名
     * */
    className: PropTypes.string,
    /**
     * 树结构值（传options就不要传子组件）
     * */
    options: PropTypes.array,
    /**
     * input placeholder
     * */
    placeholder: PropTypes.string,
    /**
     * 搜索无文本提示文字
     * */
    noResultHolder: PropTypes.string,
    /**
     * 默认选中值
     * */
    defaultValue: PropTypes.array,
    /**
     * 是否选中改变
     * */
    changeOnSelect: PropTypes.bool,
    /**
     * 是否可搜索（异步不可以搜索）
     * */
    searchable: PropTypes.bool,
    /**
     * 输入框点击回调
     * */
    onClick: PropTypes.func,
    /**
     * 值改变回调
     * */
    onChange: PropTypes.func,
    /**
     * 选中menuItem回调
     * */
    onSelect: PropTypes.func,
    /**
     * 异步获取数据函数
     * */
    loadOptionData: PropTypes.func,
    /**
     * 选项菜单显示关闭回调
     * */
    onPopVisibleChange: PropTypes.func,
    /**
     * 选中内容后展示回调
     * */
    labelRender: PropTypes.func,
    /**
     * 触发显示选中菜单元素
     * */
    anchorEl: PropTypes.element,
    /**
     * 自定义样式
     * */
    style: PropTypes.object,
    /**
     * 选择框大小，默认medium，base同medium
     * */
    size: PropTypes.oneOf(['large', 'medium', 'small', 'base']), // base === medium
    /**
     * 当为默认input输入框触发时，`Input`组件的props
     * */
    inputProps: PropTypes.object,
    /**
     * 下拉选项框类名
     * */
    pickerClass: PropTypes.string,
    /**
     * 触发方式
     * */
    trigger: PropTypes.oneOf(['click', 'focus', 'hover']),
    /**
     * options子元素key值
     * */
    childrenKeyName: PropTypes.string,
    /**
     *  scrollContainer Tooltip兼容自定义可滚动容器
     */
    scrollContainer: PropTypes.node,
  };

  static defaultProps = {
    prefixCls: 'ne-cascader-picker',
    placeholder: '',
    size: 'medium',
    changeOnSelect: false,
    searchable: false,
    defaultValue: [],
    noResultHolder: 'Not Found',
    trigger: 'focus',
    position: 'bottomLeft',
    childrenKeyName: 'children',
  };

  constructor(props) {
    super(props);
    this.options = [];
    this.updateOptions(props);
    this.selectedValue = props.defaultValue;
    this.state = {
      selectOptionMap: this.getDefaultOptions(props),
      value: props.defaultValue,
      selectedValue: props.defaultValue,
      inputValue: '',
      searchResult: [],
      defaultIsOpen: false,
      searchDefaultIsOpen: false,
    };
  }

  componentWillReceiveProps(newProps) {
    if (newProps.children !== this.props.children) {
      this.updateOptions(newProps);
    }
    if (
      newProps.defaultValue !== this.props.defaultValue ||
      (newProps.children.length && !this.props.children.length)
    ) {
      this.setState({
        value: newProps.defaultValue,
        selectedValue: newProps.defaultValue,
        selectOptionMap: this.getDefaultOptions(newProps),
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { defaultIsOpen, selectedValue, value } = this.state;
    const defaultIsOpenChanged = prevState.defaultIsOpen !== defaultIsOpen;
    if (defaultIsOpen) {
      this.searchInput && this.searchInput.focus();
      if (defaultIsOpenChanged && selectedValue !== value && !isEqualArrays(selectedValue, value)) {
        this.setState({
          selectedValue: value,
        });
      }
    }
  }

  getDefaultOptions = props => {
    const { defaultValue, childrenKeyName } = props;
    return defaultValue.length
      ? arrayTreeFilter(this.options, (o, level) => o.value === defaultValue[level], null, {
          childrenKeyName,
        })
      : [];
  };

  updateOptions = props => {
    const { searchable } = props;
    if (props.options) {
      this.options = props.options;
    } else {
      this.options = this.getTreeMapOptions(props.children);
    }
    if (searchable) {
      this.pathOptions = treeToPath(this.options);
    }
  };

  getTreeMapOptions = children =>
    React.Children.map(children, ele => {
      const props = ele.props;
      let option;
      if (ele.type.isSubMenu) {
        option = {
          value: props.value,
          label: props.title,
          disabled: props.disabled || false,
          children: this.getTreeMapOptions(props.children),
        };
      } else if (ele.type.isMenuItem) {
        option = {
          value: props.value,
          disabled: props.disabled || false,
          label: props.children,
          isLastNode: props.isLastNode !== false,
        };
      }
      return option;
    });

  onChange = (value, isLast) => {
    const { searchable, loadOptionData, childrenKeyName } = this.props;
    const selectOptionMap = this.getDefaultOptions({
      defaultValue: [...value],
    });
    this.handleValueChange(selectOptionMap, !isLast);
    if (!searchable && loadOptionData) {
      const lastOption = selectOptionMap[selectOptionMap.length - 1];
      if (lastOption.isLastNode === false && lastOption[childrenKeyName] === undefined) {
        loadOptionData(selectOptionMap.map(o => o.value), selectOptionMap);
        this.setState({
          defaultIsOpen: true,
        });
      }
    }
  };

  handleSelect = (e, value, isLast, level, selectedValue) => {
    const { onSelect } = this.props;
    if (this.searchInput) this.searchInput.focus();
    this.setState({
      selectedValue,
    });
    if (isLast) {
      this.setState({
        defaultIsOpen: false,
      });
    }
    onSelect && onSelect(e, value, isLast);
  };

  handleMenuVisibleChange = isOpen => {
    const { onPopVisibleChange } = this.props;
    if (isOpen) {
      this.setState({
        defaultIsOpen: true,
        searchDefaultIsOpen: false,
      });
    } else {
      this.setState({
        defaultIsOpen: false,
        inputValue: '',
      });
    }
    onPopVisibleChange && onPopVisibleChange(isOpen);
  };

  clearValue = e => {
    const { onChange, searchable } = this.props;
    const { inputValue } = this.state;
    // 清理搜索框值
    if (searchable && inputValue !== '') {
      this.setState({
        inputValue: '',
        searchDefaultIsOpen: false,
      });
      return;
    }
    e && e.stopPropagation && e.stopPropagation();
    // 清理组件选中值
    this.setState({
      value: [],
      selectOptionMap: [],
    });
    // 触发onChange
    onChange([], []);
  };

  handleMenuClose = () => {
    this.setState({
      inputValue: '',
      defaultIsOpen: false,
      searchDefaultIsOpen: false,
    });
  };

  handleSearch = e => {
    const inputValue = e.target.value;
    this.setState(
      {
        inputValue,
        searchDefaultIsOpen: inputValue.length > 0,
        ...(inputValue === '' && { selectOptionMap: [], value: [], selectedValue: []}),
      },
      () => {
        const result = [];
        const inputValueArr = inputValue.split('/')
        this.pathOptions.forEach(item => {
          let i = 0;
          for (const o of item) {
            const label = o.label;
            if (label.match(inputValueArr[i]) !== null ) {
              if(i === inputValueArr.length - 1) {
                result.push(item);
                break;
              } else {
                i++
              }
            }
          }
        });
        this.setState({
          searchResult: result,
        });
        inputValue === '' && this.props.onChange([], [])
      },
    );
  };

  handleSearchResultSelect = (e, value) => {
    const { searchResult } = this.state;
    const result = searchResult[value];
    this.handleValueChange(result, false);
  };

  handleValueChange = (optionMap, isOpen) => {
    const { onChange } = this.props;
    const newValue = optionMap.map(o => o.value);
    this.setState((prevState, props) => {
      if (!isEqualArrays(prevState.value, newValue)) {
        onChange && onChange(newValue, optionMap);
        return {
          value: newValue,
          selectOptionMap: [...optionMap],
        };
      }
    });
    this.setState({
      defaultIsOpen: isOpen,
    });
  };

  initSearchResult = () => {
    const { noResultHolder, prefixCls } = this.props;
    const { searchResult } = this.state;
    const menuItems = searchResult.length ? (
      searchResult.map((item, index) => {
        const label = [];
        const optionValue = [];
        let disabled = false;
        item.forEach(o => {
          label.push(o.label);
          optionValue.push(o.value);
          if (o.disabled) {
            disabled = true;
          }
        });
        return (
          <MenuItem value={index} disabled={disabled} key={index}>
            {label.join('/')}
          </MenuItem>
        );
      })
    ) : (
      <MenuItem key="notFond" disabled>
        {noResultHolder}
      </MenuItem>
    );
    return (
      <Menu selectable className={`${prefixCls}-search-menu`}>
        {menuItems}
      </Menu>
    );
  };

  getMaskRef = mask => {
    this.mask = mask;
  };

  getInputRef = input => {
    this.searchInput = input;
  };

  handleClick = () => {
    const { onClick } = this.props;
    const { defaultIsOpen } = this.state;
    onClick && onClick();
    if (this.options.length) {
      this.handleMenuVisibleChange(!defaultIsOpen);
    }
  };

  renderLabel() {
    const { labelRender } = this.props;
    const { selectOptionMap } = this.state;
    const labelMap = selectOptionMap.map(o => o.label);
    return labelRender ? labelRender(labelMap) : labelMap.join('/');
  }

  renderInput() {
    const {
      prefixCls,
      className,
      placeholder,
      searchable,
      anchorEl,
      inputProps,
      style,
      size,
      fullWidth,
      status,
      clearIcon,
      errorIcon,
      loadingIcon,
      successIcon,
      showStatusIcon,
      disabled,
      clearable,
      id,
    } = this.props;
    const { defaultIsOpen, inputValue } = this.state;
    const classString = classNames(prefixCls, className);

    return anchorEl ? (
      cloneElement(anchorEl, {
        onClick: this.handleClick,
      })
    ) : (
      <Input
        id={id}
        type="text"
        className={classString}
        placeholder={placeholder}
        inputRef={this.getInputRef}
        value={inputValue || this.renderLabel()}
        disabled={disabled}
        mask={!searchable || (searchable && !defaultIsOpen) ? this.renderLabel() : undefined}
        onClick={this.handleClick}
        onChange={this.handleSearch}
        fullWidth={fullWidth}
        status={status}
        clearable={clearable}
        onClear={this.clearValue}
        clearIcon={clearIcon}
        errorIcon={errorIcon}
        loadingIcon={loadingIcon}
        successIcon={successIcon}
        showStatusIcon={showStatusIcon}
        readOnly={!searchable}
        suffix={<ExpandMore className={classNames(`${prefixCls}-arrow`)} />}
        inputProps={inputProps}
        style={style}
        size={size}
      />
    );
  }

  render() {
    const {
      prefixCls,
      pickerClass,
      children,
      changeOnSelect,
      options,
      position,
      trigger,
      defaultValue,
      searchable,
      noResultHolder,
      loadOptionData,
      labelRender,
      onChange,
      anchorEl,
      inputProps,
      size,
      status,
      clearIcon,
      errorIcon,
      loadingIcon,
      successIcon,
      showStatusIcon,
      disabled,
      id,
      scrollContainer,
      ...other
    } = this.props;
    const { defaultIsOpen, searchDefaultIsOpen, selectedValue } = this.state;
    return (
      <DropdownMenu
        noClose
        disabled={disabled}
        defaultIsOpen={defaultIsOpen}
        anchorEl={this.renderInput()}
        onSelect={searchDefaultIsOpen ? this.handleSearchResultSelect : this.handleSelect}
        onOpenChange={this.handleMenuVisibleChange}
        className={pickerClass}
        trigger={trigger}
        position={position}
        scrollContainer={scrollContainer}>
        {searchDefaultIsOpen ? (
          this.initSearchResult()
        ) : (
          <ParallelMenu
            disabled={disabled}
            className={`${prefixCls}-menu`}
            defaultValue={selectedValue}
            options={options}
            onChange={this.onChange}
            changeOnSelect={changeOnSelect}
            {...other}>
            {children}
          </ParallelMenu>
        )}
      </DropdownMenu>
    );
  }
}

export default Cascader;



// WEBPACK FOOTER //
// ./src/cascader/Cascader.jsx